import {View} from '@fantasmagames/slot/src/common/view/View';
import {Offset, Position, ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {AvalancheReel} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReel';
import {AvalancheNewSymbol} from '@fantasmagames/slot/src/logic/SpinResult';
import {WinLine} from '@fantasmagames/slot/src/logic/WinLine';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {ReelsUtils} from '@fantasmagames/slot/src/modules/reels/ReelsUtils';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {Easing} from '@fantasmagames/core/src/tween/Easing';

export class AvalancheReelsView extends View {
	public static readonly FADING_TIMER: number = 500;

	public static readonly ALL_REELS_DROPPED_OUT: string = 'AvalancheReelsView::ALL_REELS_DROPPED_OUT';
	public static readonly REEL_DROPPED_OUT: string = 'AvalancheReelsView::REEL_DROPPED_OUT';
	public static readonly ALL_REELS_DROPPED_NEW: string = 'AvalancheReelsView::ALL_REELS_DROPPED_NEW';
	public static readonly REEL_DROPPED_NEW: string = 'AvalancheReelsView::REEL_DROPPED_NEW';
	public static readonly FILLING_VOIDS_FINISHED: string = 'AvalancheReelsView::FILLING_VOIDS_FINISHED';
	public static readonly ADDING_SYMBOLS_FINISHED: string = 'AvalancheReelsView::ADDING_SYMBOLS_FINISHED';
	public static readonly REMOVING_SYMBOLS_FINISHED: string = 'AvalancheReelsView::REMOVING_SYMBOLS_FINISHED';
	public static readonly RESPIN_REMOVING_SYMBOLS_FINISHED: string = 'AvalancheReelsView::RESPIN_REMOVING_SYMBOLS_FINISHED';

	public static readonly REELS_FADING_OUT_FINISHED: string = 'AvalancheReelsView::REELS_FADING_OUT_FINISHED';
	public static readonly REELS_FADING_IN_FINISHED: string = 'AvalancheReelsView::REELS_FADING_IN_FINISHED';

	public static readonly REELS_FADING_OUT_STARTED: string = 'AvalancheReelsView::REELS_FADING_OUT_STARTED';
	public static readonly REELS_FADING_IN_STARTED: string = 'AvalancheReelsView::REELS_FADING_IN_STARTED';

	protected _reels: AvalancheReel[] = [];

	protected _reelsetContainer: PIXI.Sprite;

	protected _wasFillingVoidMessageSent: boolean = false;

	protected _currentMask: PIXI.Graphics = null;

	protected _currentReelToAddSymbolsIndex: number = 0;

	protected _isFallingFinished: boolean = true;

	protected _currentShakingTween: Tween;
	protected _currentScaleTween: Tween;

	protected _beforeShakingPosition: Position;

	public init(): void {
		super.init();

		this.setupReelSetMaskForSpinning();
	}

	public fadeOutSymbols(): void {
		this.emit(AvalancheReelsView.REELS_FADING_OUT_STARTED);
		const fadeOutTween: Tween = new Tween(this._reelsetContainer)
			.to({alpha: 0}, AvalancheReelsView.FADING_TIMER)
			.onComplete(() => {
				this.emit(AvalancheReelsView.REELS_FADING_OUT_FINISHED);
			})
			.start();
		this.updateManager.add(fadeOutTween);
	}

	public fadeInSymbols(): void {
		this.emit(AvalancheReelsView.REELS_FADING_IN_STARTED);
		const fadeOutTween: Tween = new Tween(this._reelsetContainer)
			.to({alpha: 1}, AvalancheReelsView.FADING_TIMER)
			.onComplete(() => {
				this.emit(AvalancheReelsView.REELS_FADING_IN_FINISHED);
			})
			.start();
		this.updateManager.add(fadeOutTween);
	}

	public hideSymbolsWithAlpha(): void {
		this._reelsetContainer.alpha = 0;
	}

	/**
	 * old symbols falling down
	 */
	public dropOut(): void {
		this.soundPlayer.play('reelsFallStarted');
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._reels[reelIndex].dropOldSymbolsOut();
		}
	}

	/**
	 * new symbols all reels dropping
	 * @param reels - reels to drop down
	 * @param rowsToShow - how many rows there are to show
	 */
	public dropNew(reels: GameSymbol[][], rowsToShow: number): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._reels[reelIndex].setRowsToShow(rowsToShow);
			this._reels[reelIndex].dropSymbols(reels[reelIndex]);
		}

		this.rearrangeSymbolsOrder();
	}

	/**
	 * removes certain symbols
	 * @param symbols list
	 */
	public removeSymbolsOnAvalanche(symbols: SymbolPosition[]): void {
		const lastReel: number = symbols[symbols.length - 1].x;
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._reels[reelIndex].removeAvalancheSymbols(
				this.getAllSymbolPositionsFromReel(reelIndex, symbols),
				reelIndex === lastReel ? this.onSymbolsRemovingFinshed.bind(this, symbols) : null,
			);
		}
		this.soundPlayer.play('avalancheBlockBreak');
	}

	/**
	 * drops down all the 'in-the-air" symbols
	 */
	public fillVoidAfterRemoval(): void {
		this._wasFillingVoidMessageSent = false;
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._reels[reelIndex].dropElementsToFillVoids();
		}

		if (!this.hasAnyReelsToFillVoid()) {
			this.onFillingVoidsFinished(0);
		} else {
			this.soundPlayer.play('symbolSlidedown');
		}
	}

	public addNewSymbols(symbols: AvalancheNewSymbol[]): void {
		const lastReel: number = symbols[symbols.length - 1].reel;

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._reels[reelIndex].addNewSymbols(
				this.getAllSymbolsFromReel(reelIndex, symbols),
				reelIndex === lastReel ? this.onSymbolsAddingFinshed.bind(this) : null,
			);
		}

		this.rearrangeSymbolsOrder();
	}

	public hideSymbolAt(pos: SymbolPosition): void {
		this._reels[pos.x].hideSymbolAtPos(pos.y);
	}

	public showAllSymbols(): void {
		for (const reel of this._reels) {
			reel.showAllSymbols();
		}
	}

	public setupInitialRowsToShow(rowsToShow: number): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._reels[reelIndex].setRowsToShow(rowsToShow);
		}
	}

	public setupInitialTopVisibleSymbolIndexes(initialReel: GameSymbol[][]): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._reels[reelIndex].initTopVisibleSymbolIndex(initialReel[reelIndex]);
		}
	}

	public createReelSet(reels: GameSymbol[][]): void {
		this._reelsetContainer = new PIXI.Sprite();

		this.container.children.length = 0;
		this._reels = [];

		this.container.addChild(this._reelsetContainer);

		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			const offsets: Offset = {
				x: ReelConstants.getReelsOffsets(this.viewType).x + reelIndex * ReelConstants.getSymbolSlot(this.viewType).width,
				y: ReelConstants.getReelsOffsets(this.viewType).y,
			};

			const reel: AvalancheReel = this.createReel(reelIndex, 0, offsets, this._reelsetContainer);
			reel.setupReel(this.resources, this.soundPlayer);
			reel.timerUtils = this.timeUtils;

			if (reels) {
				reel.init(reels[reelIndex]);
			} else {
				reel.init();
			}

			reel.onReelDropOutFinished = this.onReelDropOutFinished.bind(this);
			reel.onReelDropFirstSymbolFinished = this.onReelFirstSymbolDropped.bind(this);
			reel.onReelDropNewFinished = this.onReelDropNewFinished.bind(this);
			reel.onFillingVoidsFinished = this.onFillingVoidsFinished.bind(this);

			this.updateManager.add(reel);
			reel.start();
			this._reels.push(reel);
		}
		this._reels[ReelConstants.REELS_AMOUNT - 1].onReelDropOutFinished = this.onLastReelDropOutFinished.bind(this);
		this._reels[ReelConstants.REELS_AMOUNT - 1].onReelDropNewFinished = this.onLastReelDropNewFinished.bind(this);

		this.forceUpdateOrientation();

		this.setupInitialRowsToShow(ReelConstants.ROW_AMOUNT);
	}

	public rearrangeSymbolsOrder(): void {

	}

	public startAddingNewSymbolsWithExpectedWin(reels: GameSymbol[][], symbols: AvalancheNewSymbol[], winLines: WinLine[]): void {
		if (this._isFallingFinished) {
			this._isFallingFinished = false;
			this._currentReelToAddSymbolsIndex = 0;
			this.addNewSymbolsWithExpectedWin(reels, symbols, winLines);
		}
	}

	public recountSymbolsOnReels(): void {
		for (let reel: number = 0; reel < ReelConstants.REELS_AMOUNT; reel++) {
			this._reels[reel].recountSymbolsOnReel();
		}
	}

	protected getWinSituationForSym(symPosition: SymbolPosition, winLines: WinLine[]): WinLine {
		for (const wl of winLines) {
			for (let i = 0; i < wl.positions.length; i++) {
				const pos = wl.positions[i].x;
				if (pos === symPosition.x && i === symPosition.y) {
					return wl;
				}
			}
		}

		return null;
	}

	protected getAllPositionsFromReel(positions: SymbolPosition[], reelIndex: number): SymbolPosition[] {
		const reelPositions: SymbolPosition[] = [];

		for (const pos of positions) {
			if (pos.x === reelIndex) {
				reelPositions.push(pos);
			}
		}

		return reelPositions;
	}

	protected getNumberSymbolsInArray(reelIndex: number, syms: SymbolPosition[]): number {
		let value: number = 0;
		for (const pos of syms) {
			if (pos.x === reelIndex) {
				value++;
			}
		}

		return value;
	}

	protected addNewSymbolsWithExpectedWin(reels: GameSymbol[][], symbols: AvalancheNewSymbol[], winLines: WinLine[]): void {
		const lastReel: number = symbols[symbols.length - 1].reel;

		this.addReelSymbols(
			this._currentReelToAddSymbolsIndex,
			symbols,
			winLines,
			reels,
			() => {
				if (!this._isFallingFinished) {
					this._currentReelToAddSymbolsIndex++;
					if (this._currentReelToAddSymbolsIndex <= lastReel) {
						this.addNewSymbolsWithExpectedWin(reels, symbols, winLines);
					}
				}
			},
			() => {
				this._currentReelToAddSymbolsIndex++;
				if (this._currentReelToAddSymbolsIndex <= lastReel) {
					this.addNewSymbolsWithExpectedWin(reels, symbols, winLines);
				}
			},
			(fallenReel: number) => {
				if (fallenReel === lastReel) {
					this._isFallingFinished = true;
					this._currentReelToAddSymbolsIndex = 0;
					this.onSymbolsAddingFinshed && this.onSymbolsAddingFinshed();
				}
			},
		);
	}

	/**
	 * adding symbols on speceific reel
	 * @param reelIndex
	 * @param symbols - symbols to add
	 * @param winLines - all the win lines
	 * @param currentReels - reels
	 * @param onExpectedWinAnimationCallback
	 * @param onNoWinAnimationCallback
	 * @param onSymbolFallenCallback
	 */
	protected addReelSymbols(
		reelIndex: number,
		symbols: AvalancheNewSymbol[],
		winLines: WinLine[],
		currentReels: GameSymbol[][],
		onExpectedWinAnimationCallback: Function,
		onNoWinAnimationCallback: Function,
		onSymbolFallenCallback: Function,
	): void {
		const reel: AvalancheReel = this._reels[reelIndex];

		const winSymbols: SymbolPosition[] = this.getAllWinningSymbolsBeforeReel(reelIndex, winLines);
		const newSymbols: AvalancheNewSymbol[] = this.getAllSymbolsFromReel(reelIndex, symbols);

		reel.addNewSymbols(newSymbols, () => {
			onSymbolFallenCallback && onSymbolFallenCallback(reelIndex);
			if (winSymbols && winSymbols.length > 0 && reelIndex > 1) {
				this.tryToPlayExpectedWin(reelIndex, currentReels, winSymbols, newSymbols, onExpectedWinAnimationCallback);
			}
		});

		this.rearrangeSymbolsOrder();

		if (winSymbols.length === 0 || reelIndex < 2 || (reelIndex >= 2 && !this.hasAnyOfSymbolsInWinLine(newSymbols, winSymbols))) {
			onNoWinAnimationCallback && onNoWinAnimationCallback();
		}
	}

	protected getAllWinningSymbolsBeforeReel(reelIndex: number, winLines: WinLine[]): SymbolPosition[] {
		const winPositions: SymbolPosition[] = [];

		for (const wl of winLines) {
			for (const pos of wl.positions) {
				if (pos.x <= reelIndex) {
					winPositions.push({x: pos.x, y: pos.y});
				}
			}
		}

		return winPositions;
	}

	protected tryToPlayExpectedWin(
		reelIndex: number,
		currentReels: GameSymbol[][],
		winSymbols: SymbolPosition[],
		newSymbols: AvalancheNewSymbol[],
		onExpectedWinAnimationCallback: Function,
	): void {
		for (const symPos of newSymbols) {
			if (!this.isSymbolInWinLine({x: symPos.reel, y: symPos.row}, winSymbols)) {
				continue;
			}

			let isFirst: boolean = true;
			for (const sym of winSymbols) {
				const reelSym: GameSymbol = currentReels[sym.x][sym.y];
				if (!reelSym) {
					continue;
				}

				const animationPosition: Position = ReelsUtils.getPositionOnReels(sym, currentReels);
				animationPosition.y += (ReelConstants.getSymbolSlot(this.viewType).height / 2) * (reelSym.size.height - 1);
				const preWinAnimation: SpineAnimation = this.createExpectedWinAnimation(reelSym.size.height, animationPosition);

				if (isFirst) {
					isFirst = false;

					this.soundPlayer.play('expectedWinReelComingDown');
					onExpectedWinAnimationCallback && preWinAnimation.setOnComplete(onExpectedWinAnimationCallback);
				}
			}
		}
	}

	protected createExpectedWinAnimation(symSize: number, position: Position): SpineAnimation {
		return this.spineUtils
			.startSpineAnimationCreation('WIN.json', `EXPECT_WIN_WIN_${symSize}`, false)
			.container(this.container)
			.position(position)
			.createAndStart();
	}

	protected hasAnyOfSymbolsInWinLine(positions: AvalancheNewSymbol[], winLines: SymbolPosition[]): boolean {
		for (const symPos of positions) {
			for (const wl of winLines) {
				if (symPos.reel === wl.x && symPos.row === wl.y) {
					return true;
				}
			}
		}

		return false;
	}

	protected isSymbolInWinLine(symPos: SymbolPosition, winLine: SymbolPosition[]): boolean {
		for (const wp of winLine) {
			if (wp.y === symPos.y && wp.x === symPos.x) {
				return true;
			}
		}

		return false;
	}

	protected setupReelSetMaskForSpinning(): void {
		if (this.resources.hasMajorPerformanceCaveat) {
			this._currentMask = null;

			return;
		}

		this._currentMask = new PIXI.Graphics();

		this._currentMask.drawRect(0, 0, 1, 1);

		this._currentMask.x = 0;
		this._currentMask.y = 0;
		this._currentMask.width = this.resolutionManager.resolution.width;
		this._currentMask.height = this.resolutionManager.resolution.height;

		this.container.addChild(this._currentMask);

		this.container.mask = this._currentMask;
	}

	protected hasAnyReelsToFillVoid(): boolean {
		return false;
	}

	protected onSymbolsAddingFinshed(): void {
		this.emit(AvalancheReelsView.ADDING_SYMBOLS_FINISHED);
	}

	protected onSymbolsRemovingFinshed(symbols: SymbolPosition[]): void {
		this.emit(AvalancheReelsView.REMOVING_SYMBOLS_FINISHED, symbols);
	}

	protected getAllSymbolsFromReel(reelIndex: number, symbols: AvalancheNewSymbol[]): AvalancheNewSymbol[] {
		const syms: AvalancheNewSymbol[] = [];
		for (const sym of symbols) {
			if (sym.reel === reelIndex) {
				syms.push(sym);
			}
		}

		return syms;
	}

	protected getAllSymbolPositionsFromReel(reelIndex: number, symbols: SymbolPosition[]): SymbolPosition[] {
		const syms: SymbolPosition[] = [];
		for (const sym of symbols) {
			if (sym.x === reelIndex) {
				syms.push(sym);
			}
		}

		return syms;
	}

	protected createReel(reelIndex: number, startReelPosition: number, offsets: SymbolPosition, container: PIXI.Sprite): AvalancheReel {
		throw new Error('AvalancheReelsView::createReel should be implemented for the game');
	}

	protected onReelDropOutFinished(reelNr: number): void {
		this.emit(AvalancheReelsView.REEL_DROPPED_OUT, reelNr);
	}

	protected onReelFirstSymbolDropped(reelNr: number): void {

	}

	protected onLastReelDropOutFinished(reelNr: number): void {
		this.emit(AvalancheReelsView.ALL_REELS_DROPPED_OUT, reelNr);
	}

	protected onReelDropNewFinished(reelNr: number): void {
		this.emit(AvalancheReelsView.REEL_DROPPED_NEW, reelNr);
	}

	protected onLastReelDropNewFinished(reelNr: number): void {
		this.emit(AvalancheReelsView.ALL_REELS_DROPPED_NEW, reelNr);
	}

	protected onFillingVoidsFinished(reelNr: number): void {
		if (!this._wasFillingVoidMessageSent) {
			this.emit(AvalancheReelsView.FILLING_VOIDS_FINISHED);
			this._wasFillingVoidMessageSent = true;
		}
	}

	public shakeReels(power: number = 1, repeats: number = NaN): void {
		this._beforeShakingPosition = {x: this.container.x, y: this.container.y};

		this.stopShaking();

		const duration: number = Math.ceil(50 * power);
		let totalRepeats: number = Math.ceil(6 * power);
		if (!isNaN(repeats)) {
			totalRepeats = repeats;
		}
		const basePos: SymbolPosition = {x: this.resolutionManager.halfResolution.width, y: this.resolutionManager.halfResolution.height};
		this.container.pivot.set(basePos.x, basePos.y);

		const strength: number = 10 * power;

		const getValues = (): SymbolPosition => {
			return {x: (Math.random() - 0.5) * strength + basePos.x, y: (Math.random() - 0.5) * strength + basePos.y};
		};

		this.container.position.set(getValues().x, getValues().y);

		const shakeTween: Tween = new Tween(this.container)
			.to(getValues(), duration / totalRepeats)
			.repeat(totalRepeats)
			.yoyo(true)
			.easing(Easing.Linear.None)
			.onRepeat(() => {
				if (shakeTween.getRepeat() < totalRepeats) {
					shakeTween.to(getValues(), duration / (shakeTween.getRepeat() * shakeTween.getRepeat()));
				}
			})
			.onComplete(() => {
				shakeTween.to({x: basePos.x, y: basePos.y}, duration);
			})
			.start();
		this.updateManager.add(shakeTween);

		this._currentShakingTween = shakeTween;

		const scaleTween: Tween = new Tween(this.container.scale)
			.to({x: [Math.pow(1.03, power), 1], y: [Math.pow(1.03, power), 1]}, 250)
			.start();
		this.updateManager.add(scaleTween);

		this._currentScaleTween = scaleTween;
	}

	public stopShaking(): void {
		if (this._currentShakingTween) {
			this._currentShakingTween.stop();
			this.updateManager.remove(this._currentShakingTween);
			this._currentShakingTween = null;
		}

		if (this._currentScaleTween) {
			this._currentScaleTween.stop();
			this.updateManager.remove(this._currentScaleTween);
			this._currentScaleTween = null;
		}

		this.container.scale.set(1.0);
		const basePos: SymbolPosition = {x: this.resolutionManager.halfResolution.width, y: this.resolutionManager.halfResolution.height};
		this.container.pivot.set(basePos.x, basePos.y);
		this.container.position.set(basePos.x, basePos.y);
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsView.ts